import { useState, useEffect } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';

const TicketModal = ({ open, onClose, submitter, boothNumbers, onSave }) => {
  const [ticketDetails, setTicketDetails] = useState({
    createdBy: '',
    boothNumber: '',
    issue: '',
    actionTaken: '',
    notes: '',
  });
  const [showErrors, setShowErrors] = useState({
    boothNumber: false,
    issue: false,
    actionTaken: false,
  });
  const [axiosError, setAxiosError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTicketDetails({
        createdBy: submitter || '',
        boothNumber: '',
        issue: '',
        actionTaken: '',
        notes: '',
      });
      setShowErrors({
        boothNumber: false,
        issue: false,
        actionTaken: false,
      });
      setAxiosError('');
      setLoading(false);
    }
  }, [open, submitter]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSave = () => {
    setLoading(true);
    setAxiosError('');
    const finalTicketDetails = { ...ticketDetails, createdBy: submitter || '' };
    onSave(finalTicketDetails)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        setLoading(false);
        setAxiosError(
          error?.response?.data?.message ?? 'An error occurred while submitting the ticket'
        );
      });
  };

  const checkIsEmpty = (fieldName) => {
    if (!ticketDetails[fieldName]) {
      setShowErrors((prevErrors) => ({ ...prevErrors, [fieldName]: true }));
    }
  };

  const handleInputChange = (field, value) => {
    setTicketDetails((prevDetails) => ({
      ...prevDetails,
      [field]: value,
    }));
    if (showErrors[field] && value) {
      setShowErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
    }
  };

  const validateAndSave = () => {
    const requiredFields = ['boothNumber', 'issue', 'actionTaken'];
    let hasError = false;
    const currentErrors = {
      boothNumber: false,
      issue: false,
      actionTaken: false,
    };

    requiredFields.forEach((field) => {
      if (!ticketDetails[field] || String(ticketDetails[field]).trim() === '') {
        currentErrors[field] = true;
        hasError = true;
      }
    });

    setShowErrors(currentErrors);

    if (!hasError) {
      handleSave();
    }
  };

  const actionTakenOptions = [
    { value: 'Verbal Warning', label: 'Verbal Warning' },
    { value: 'Removed Firing Pin', label: 'Removed Firing Pin' },
    { value: 'Removed from Show Floor', label: 'Removed from Show Floor' },
    { value: 'Reported to Show Management', label: 'Reported to Show Management' },
    { value: 'Follow Up Required', label: 'Follow Up Required' },
    { value: 'Other', label: 'Other' },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create Ticket</DialogTitle>
      <Divider sx={{ mx: 3 }} />
      <DialogContent>
        {axiosError && (
          <Typography variant='body1' color='error' sx={{ mb: 0.5, textAlign: 'center' }}>
            Error: {axiosError}
          </Typography>
        )}
        <Typography
          variant='body1'
          color={
            Object.values(showErrors).some((value) => value === true) ? 'error' : 'textSecondary'
          }
          sx={{ mb: 1.5 }}
        >
          * Required
        </Typography>
        <TextField
          fullWidth
          label='Submitted By'
          value={ticketDetails.createdBy}
          disabled
          sx={{ mt: 1, mb: 1 }}
          InputProps={{
            readOnly: true,
          }}
        />
        <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
          <Autocomplete
            options={boothNumbers || []}
            getOptionLabel={(option) => option}
            value={ticketDetails.boothNumber || null}
            onChange={(event, newValue) => {
              handleInputChange('boothNumber', newValue || '');
            }}
            onBlur={() => checkIsEmpty('boothNumber')}
            renderInput={(params) => (
              <TextField {...params} label='Booth #' required error={showErrors.boothNumber} />
            )}
          />
        </FormControl>
        <TextField
          fullWidth
          label='Issue'
          required
          value={ticketDetails.issue}
          onChange={(e) => handleInputChange('issue', e.target.value)}
          onBlur={() => checkIsEmpty('issue')}
          error={showErrors.issue}
          sx={{ mt: 1, mb: 1 }}
        />
        <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
          <Autocomplete
            value={
              actionTakenOptions.find((option) => option.value === ticketDetails.actionTaken) ||
              null
            }
            onChange={(event, newValue) => {
              handleInputChange('actionTaken', newValue ? newValue.value : '');
            }}
            onBlur={() => checkIsEmpty('actionTaken')}
            options={actionTakenOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label='Action Taken' required error={showErrors.actionTaken} />
            )}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
          />
        </FormControl>
        <TextField
          fullWidth
          label='Notes'
          multiline
          rows={3}
          value={ticketDetails.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          sx={{ mt: 1, mb: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ pb: 2, pr: 2 }}>
        <Button onClick={handleClose} color='rendezvousRed'>
          Cancel
        </Button>
        <Button
          onClick={validateAndSave}
          color='rendezvousRed'
          variant='contained'
          disabled={loading}
          endIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketModal;
